import { useEffect, useRef } from 'react';
import { useStore } from '../store/StoreContext.jsx';
import { useLayout } from '../store/LayoutContext.jsx';
import Icon from './Icon.jsx';
import ViewButton from './ViewButton.jsx';

// The advisor's private notes, docked to the bottom of the window on every
// page and at every width. It began as Zoom mode's answer to the floating
// panel, which at 76vw covered the form it was meant to sit beside; docked,
// the notes take a band of height instead of a band of width, so the form
// stays readable above and the advisor types below. With the PQ split into
// tabs that is the one place the notes can live and be the same on each page,
// so the float and the tile under the old side nav are gone.
//
// Two states, both persisted: a collapsed bar (a one-line handle, always on
// screen) and open at a dragged height. Ctrl+Q toggles it, and so does the
// Notes button in the tab bar. Bound to advisorNotes, the private field.
export default function NotesDock() {
  const { data, update, readOnly } = useStore();
  const {
    notesOpen: open,
    setNotesOpen: setOpen,
    notesHeight: height,
    setNotesHeight: setHeight,
  } = useLayout();
  const inputRef = useRef(null);
  const dragRef = useRef(null);

  // The keydown listener is registered once, so it reads the live state from
  // a ref rather than closing over a stale render.
  const openRef = useRef(open);
  openRef.current = open;

  // Ctrl+Q toggles. Esc collapses, but only while the cursor is in the notes:
  // the dock is open most of the time in this mode, so a bare Esc pressed to
  // dismiss a date picker must not take the notes away with it.
  useEffect(() => {
    const onKey = e => {
      const q = e.key === 'q' || e.key === 'Q';
      if (q && (e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey) {
        e.preventDefault();
        setOpen(!openRef.current);
        return;
      }
      if (e.key === 'Escape' && openRef.current && document.activeElement === inputRef.current) {
        e.preventDefault();
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setOpen]);

  // Opening from the keyboard puts the cursor in the notes, so Ctrl+Q then
  // typing works without reaching for the mouse.
  const openedByKey = useRef(false);
  useEffect(() => {
    if (open && openedByKey.current) inputRef.current?.focus();
    openedByKey.current = false;
  }, [open]);

  // Resize by dragging the top edge. Pointer capture keeps the drag alive when
  // the cursor outruns the handle, and means nothing to clean up on the window.
  const startDrag = e => {
    dragRef.current = { y: e.clientY, h: height };
    e.currentTarget.setPointerCapture(e.pointerId);
    e.preventDefault();
  };
  const onDrag = e => {
    const d = dragRef.current;
    if (!d) return;
    // Dragging up (smaller clientY) makes the dock taller.
    setHeight(d.h + (d.y - e.clientY));
  };
  const endDrag = e => {
    if (!dragRef.current) return;
    dragRef.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* already gone */ }
  };

  return (
    <div
      className={'notes-dock' + (open ? ' is-open' : '')}
      role="region"
      aria-label="Notes"
      style={open ? { '--dock-body-h': `${height}px` } : undefined}
    >
      {open && (
        <div
          className="notes-dock-grip"
          onPointerDown={startDrag}
          onPointerMove={onDrag}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          title="Drag to resize"
          aria-hidden="true"
        />
      )}
      {/* The whole bar is the toggle when collapsed. When open the bar is a
          plain header and only the chevron collapses it, so a click meant for
          the header (the usual place to grab and drag) does not close it. */}
      <div
        className="notes-dock-head"
        onClick={open ? undefined : () => setOpen(true)}
        role={open ? undefined : 'button'}
        tabIndex={open ? undefined : 0}
        onKeyDown={open ? undefined : e => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(true); }
        }}
      >
        <span className="notes-dock-title">Notes</span>
        <span className="notes-dock-hint">
          {open ? 'Not shown to the client · Ctrl+Q to hide' : 'Ctrl+Q'}
        </span>
        <ViewButton
          className="panel-toggle notes-dock-toggle"
          onClick={e => { e.stopPropagation(); setOpen(!open); }}
          aria-expanded={open}
          aria-label={open ? 'Collapse notes' : 'Expand notes'}
          title={open ? 'Collapse notes  (Ctrl+Q)' : 'Expand notes  (Ctrl+Q)'}
        >
          <Icon name={open ? 'chevron-down' : 'chevron-up'} size={14} />
        </ViewButton>
      </div>
      {open && (
        <textarea
          ref={inputRef}
          className="notes-dock-input"
          placeholder="Your notes. Not shown to the client…"
          value={data.goals?.advisorNotes ?? ''}
          disabled={readOnly}
          onChange={e => update('goals.advisorNotes', e.target.value)}
        />
      )}
    </div>
  );
}
